import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { styled } from "@mui/material/styles";
import { Typography, Box, Button, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";

import useResponsive from "../hooks/useResponsive";
import Logo from "../components/Logo";
import SeverErrorIllustration from "../components/SeverErrorIllustration";
import { verifyEmail } from "../AuthenticationData.ts";
import SimpleFooter from "../components/SimpleFooter";

const StyledPageContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const ContentWrapper = styled("div")(({ theme }) => ({
  flexGrow: 1,
  display: "flex",
  flexDirection: "column",

  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
  },
}));

const StyledSection = styled("div")(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: {
    width: "40%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3, 4),
    backgroundColor: theme.palette.background.default,
  },
}));

const StyledContent = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  padding: theme.spacing(8, 3, 2, 3),
  overflowY: "auto",
  [theme.breakpoints.up("md")]: {
    width: "35%",
    alignItems: "center",
    padding: theme.spacing(3, 4),
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 480,
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(6, 5),
    boxShadow: theme.shadows[8],
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

const statusHeadings = [
  "Securing your account...",
  "Finalizing your profile...",
  "One moment, please...",
  "Almost there!",
];

const statusSubheadings = [
  "This will only take a second.",
  "We're just confirming your details.",
  "Thanks for your patience.",
  "Getting everything ready for you.",
];

const getRandomMessages = () => {
  const heading =
    statusHeadings[Math.floor(Math.random() * statusHeadings.length)];
  const subheading =
    statusSubheadings[Math.floor(Math.random() * statusSubheadings.length)];
  return { heading, subheading };
};

export default function VerifyingMail() {
  const navigate = useNavigate();
  const { token } = useParams();
  const mdUp = useResponsive("up", "md");

  const [verificationStatus, setVerificationStatus] = useState("pending");
  const [welcomeMessages] = useState(getRandomMessages);

  useEffect(() => {
    if (token) {
      verify();
    } else {
      setVerificationStatus("error");
    }
  }, [token]);

  const verify = async () => {
    setTimeout(async () => {
      try {
        const response = await verifyEmail(token);
        if (response && !response.error) {
          setVerificationStatus("success");
        } else {
          setVerificationStatus("error");
        }
      } catch (error) {
        console.error("Error verifying email:", error);
        setVerificationStatus("error");
      }
    }, 1500);
  };

  const renderStatusContent = () => {
    switch (verificationStatus) {
      case "success":
        return (
          <Box sx={{ textAlign: "center" }}>
            <SeverErrorIllustration
              sx={{ height: 180, mb: 3, display: { md: "none" } }}
            />
            <Typography variant="h4" gutterBottom>
              Success!
            </Typography>
            <Typography sx={{ color: "text.secondary", mb: 3 }}>
              Your email has been verified. Welcome to Contactie!
            </Typography>
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={() => navigate("/login")}
            >
              Go to Login
            </Button>
          </Box>
        );
      case "error":
        return (
          <Box sx={{ textAlign: "center" }}>
            <SeverErrorIllustration
              sx={{ height: 180, mb: 3, display: { md: "none" } }}
            />
            <Typography variant="h4" color="error" gutterBottom>
              Verification Failed
            </Typography>
            <Typography sx={{ color: "text.secondary", mb: 3 }}>
              This verification link may be invalid or has expired. Please
              return to the login page to request a new link.
            </Typography>
            <Button
              fullWidth
              size="large"
              variant="contained"
              onClick={() => navigate("/login")}
            >
              Return to Login
            </Button>
          </Box>
        );
      case "pending":
      default:
        return (
          <Box sx={{ textAlign: "center" }}>
            <SeverErrorIllustration
              sx={{ height: 180, mb: 3, display: { md: "none" } }}
            />
            <CircularProgress sx={{ mb: 3 }} />
            <Typography variant="h6">Verifying...</Typography>
          </Box>
        );
    }
  };

  return (
    <>
      <Helmet>
        <title>Contactie | Verifying Email</title>
        <meta
          name="description"
          content="Verifying your Contactie account email address. Please wait a moment."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <StyledPageContainer>
        <ContentWrapper>
          {" "}
          <Box
            component="header"
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              zIndex: 10,
              top: 40,
              left: 40,
            }}
          >
            <Logo sx={{ height: 30, width: "auto" }} />
          </Box>
          {mdUp && (
            <StyledSection>
              <Typography
                variant="h4"
                sx={{ textAlign: "center", mb: 2, px: 5 }}
              >
                {welcomeMessages.heading}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", textAlign: "center" }}
              >
                {welcomeMessages.subheading}
              </Typography>
              <SeverErrorIllustration
                sx={{
                  width: "100%",
                  maxWidth: 320,
                  maxHeight: "50vh",
                  objectFit: "contain",
                  mt: 8,
                }}
              />
            </StyledSection>
          )}
          <StyledContent>
            <FormContainer>
              <Box
                sx={{
                  mb: { xs: 5, md: 3 },
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                <Logo
                  sx={{
                    height: 35,
                    width: "auto",
                    mb: 3,
                    display: { xs: "inline-block", md: "none" },
                  }}
                />

                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ display: { xs: "none", md: "block" }, fontWeight: 700 }}
                >
                  Verification Status
                </Typography>

                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{ display: { xs: "block", md: "none" } }}
                >
                  {verificationStatus === "success"
                    ? "All Set!"
                    : verificationStatus === "error"
                      ? "Verification Error"
                      : "Just a moment..."}
                </Typography>
              </Box>

              {renderStatusContent()}
            </FormContainer>
          </StyledContent>
        </ContentWrapper>{" "}
        <SimpleFooter />
      </StyledPageContainer>{" "}
    </>
  );
}
